{
    "sections": {
        "identity": {
            "label": "Member and patient identity",
            "help_text": "Verify member and patient identity facts."
        },
        "eligibility_and_sep": {
            "label": "Eligibility, referral, and SEP",
            "help_text": "Review printed eligibility, referral, entitlement, and SEP facts."
        },
        "claim_identity": {
            "label": "Claim identity and lineage",
            "help_text": "Review claim, provider, account, invoice, and correction identifiers."
        },
        "encounter": {
            "label": "Encounter and care timeline",
            "help_text": "Review the documented care episode and timeline."
        },
        "diagnosis_information": {
            "label": "Diagnoses",
            "help_text": "Review documented ICD-10 2010 diagnoses and explicit roles."
        },
        "procedure_information": {
            "label": "Procedures",
            "help_text": "Review documented ICD-9-CM 2010 procedures and services."
        },
        "clinical_claim_facts": {
            "label": "Clinical claim facts",
            "help_text": "Review supported clinical measurements and episode facts."
        },
        "billing": {
            "label": "Billing",
            "help_text": "Review hospital charges and submitted IDR amounts without inferring an insurer decision."
        },
        "grouping": {
            "label": "INA-CBG grouping",
            "help_text": "Review printed grouping values without predicting a missing result."
        },
        "supporting_documents": {
            "label": "Supporting documents",
            "help_text": "Review document presence, labels, identifiers, and unresolved conflicts."
        }
    },
    "fields": {
        "identity_patient_name": {
            "label": "Patient name",
            "help_text": "Patient name as written."
        },
        "identity_bpjs_card_number": {
            "label": "BPJS card number",
            "help_text": "BPJS participant or card number."
        },
        "identity_nik": {
            "label": "NIK",
            "help_text": "National identity number when printed. Preserve its source formatting."
        },
        "identity_medical_record_number": {
            "label": "Medical record number",
            "help_text": "Hospital medical-record identifier."
        },
        "identity_date_of_birth": {
            "label": "Date of birth",
            "help_text": "Patient date of birth."
        },
        "identity_sex": {
            "label": "Gender",
            "help_text": "Patient gender exactly as printed."
        },
        "eligibility_and_sep_participant_type": {
            "label": "Participant type",
            "help_text": "BPJS/JKN participant category."
        },
        "eligibility_and_sep_participant_status": {
            "label": "Participant status",
            "help_text": "Printed membership or eligibility status; not a live eligibility decision."
        },
        "eligibility_and_sep_entitled_class": {
            "label": "Entitled class",
            "help_text": "Documented BPJS entitlement class."
        },
        "eligibility_and_sep_referral_number": {
            "label": "Referral number",
            "help_text": "Referral identifier as printed."
        },
        "eligibility_and_sep_primary_care_provider": {
            "label": "Primary care provider",
            "help_text": "Documented referring or registered primary-care provider."
        },
        "eligibility_and_sep_guarantee_number": {
            "label": "Guarantee number",
            "help_text": "Documented guarantee-letter identifier."
        },
        "eligibility_and_sep_guarantee_issued_at": {
            "label": "Guarantee issue time",
            "help_text": "Guarantee-letter issue date and time when printed; do not infer it from admission."
        },
        "eligibility_and_sep_sep_records": {
            "label": "SEP records",
            "help_text": "All SEP references visible in the packet; no one-to-one relationship is assumed."
        },
        "claim_identity_claim_number": {
            "label": "Claim number",
            "help_text": "Claim identifier as printed."
        },
        "claim_identity_batch_number": {
            "label": "Batch number",
            "help_text": "Batch identifier as printed."
        },
        "claim_identity_facility_code": {
            "label": "Facility code",
            "help_text": "Hospital or provider code."
        },
        "claim_identity_facility_name": {
            "label": "Facility name",
            "help_text": "Hospital or provider name."
        },
        "claim_identity_account_number": {
            "label": "Account number",
            "help_text": "Hospital account identifier."
        },
        "claim_identity_invoice_number": {
            "label": "Invoice number",
            "help_text": "Hospital invoice identifier."
        },
        "claim_identity_claim_instances": {
            "label": "Claim instances",
            "help_text": "Linked original, corrected, or resubmitted claim instances; cardinality is unconfirmed."
        },
        "claim_identity_correction_references": {
            "label": "Correction references",
            "help_text": "Correction and claim-lineage references without adjudication conclusions."
        },
        "encounter_service_type": {
            "label": "Service type",
            "help_text": "Documented inpatient, outpatient, or other service setting."
        },
        "encounter_claim_pathway": {
            "label": "Claim pathway",
            "help_text": "Documented BPJS claim pathway or separately paid category; do not infer a pathway from a charge alone."
        },
        "encounter_admission_datetime": {
            "label": "Admission time",
            "help_text": "Admission date and time."
        },
        "encounter_discharge_datetime": {
            "label": "Discharge time",
            "help_text": "Discharge date and time."
        },
        "encounter_discharge_disposition": {
            "label": "Discharge disposition",
            "help_text": "Discharge disposition exactly as documented."
        },
        "encounter_treatment_class": {
            "label": "Treatment class",
            "help_text": "Actual documented treatment class."
        },
        "encounter_room_or_ward": {
            "label": "Room or ward",
            "help_text": "Documented room, ward, or unit."
        },
        "encounter_dpjp_name": {
            "label": "DPJP",
            "help_text": "Responsible physician name exactly as documented."
        },
        "encounter_length_of_stay_days": {
            "label": "Length of stay",
            "help_text": "Documented length of stay in days; do not derive when missing."
        },
        "encounter_care_setting": {
            "label": "Care setting",
            "help_text": "Care setting exactly as documented, such as emergency, outpatient, or inpatient."
        },
        "encounter_episodes": {
            "label": "Episodes",
            "help_text": "All episode references visible in the packet; no one-to-one relationship is assumed."
        },
        "encounter_referrals": {
            "label": "Referral records",
            "help_text": "Referral records visible in the claim packet; no one-to-one relationship is assumed."
        },
        "encounter_events": {
            "label": "Encounter events",
            "help_text": "Ordered encounter, consultation, test, and result events visible in reviewed claim records."
        },
        "encounter_transport_records": {
            "label": "Transport records",
            "help_text": "Referral transport records visible in the claim packet; do not infer transport from a charge label alone."
        },
        "diagnosis_information_diagnoses": {
            "label": "Diagnoses",
            "help_text": "Documented diagnoses using ICD-10 2010 when coded."
        },
        "procedure_information_procedures": {
            "label": "Procedures",
            "help_text": "Documented procedures using ICD-9-CM 2010 when coded."
        },
        "clinical_claim_facts_birth_weight_grams": {
            "label": "Birth weight",
            "help_text": "Documented birth weight in grams."
        },
        "clinical_claim_facts_icu_days": {
            "label": "ICU days",
            "help_text": "Documented ICU duration in days."
        },
        "clinical_claim_facts_ventilator_hours": {
            "label": "Ventilator hours",
            "help_text": "Documented ventilator duration in hours."
        },
        "clinical_claim_facts_whodas_score": {
            "label": "WHODAS score",
            "help_text": "Documented WHODAS score when applicable."
        },
        "clinical_claim_facts_whodas_assessment_version": {
            "label": "WHODAS version",
            "help_text": "WHODAS instrument version exactly as documented."
        },
        "clinical_claim_facts_whodas_variable_scores": {
            "label": "WHODAS variable scores",
            "help_text": "WHODAS variable scores as documented; do not calculate a score from missing variables."
        },
        "clinical_claim_facts_whodas_assessment_date": {
            "label": "WHODAS assessment date",
            "help_text": "WHODAS assessment date exactly as documented."
        },
        "clinical_claim_facts_whodas_assessment_phase": {
            "label": "WHODAS assessment phase",
            "help_text": "WHODAS assessment phase exactly as documented."
        },
        "billing_total_charges": {
            "label": "Total charges",
            "help_text": "Documented hospital charge total, not an insurer determination."
        },
        "billing_submitted_amount": {
            "label": "Submitted amount",
            "help_text": "Amount documented as submitted on the claim."
        },
        "billing_participant_liability": {
            "label": "Participant liability",
            "help_text": "Participant liability exactly as printed; do not infer a final obligation."
        },
        "billing_invoice_date": {
            "label": "Invoice date",
            "help_text": "Documented invoice date."
        },
        "billing_charge_items": {
            "label": "Charge items",
            "help_text": "Detailed hospital charge rows."
        },
        "billing_invoice_line_items": {
            "label": "Invoice line items",
            "help_text": "Invoice recap rows kept distinct from clinical services."
        },
        "billing_category_totals": {
            "label": "Billing category totals",
            "help_text": "Documented billing totals by category."
        },
        "billing_payment_basis": {
            "label": "Payment family",
            "help_text": "INA-CBG or Non-INA-CBG payment family explicitly shown in the reviewed claim record."
        },
        "grouping_inacbg_code": {
            "label": "INA-CBG code",
            "help_text": "Printed INA-CBG code; never predict a missing code."
        },
        "grouping_inacbg_description": {
            "label": "INA-CBG description",
            "help_text": "Printed INA-CBG description."
        },
        "grouping_grouper_version": {
            "label": "Grouper version",
            "help_text": "Printed INA-CBG grouper version."
        },
        "grouping_tariff_class": {
            "label": "Tariff class",
            "help_text": "Printed tariff class associated with grouping."
        },
        "grouping_tariff_amount": {
            "label": "Grouped tariff amount",
            "help_text": "Printed INA-CBG grouped tariff amount, not a final insurer determination."
        },
        "grouping_special_cmg_code": {
            "label": "Special CMG code",
            "help_text": "Printed Special CMG code when present."
        },
        "grouping_results": {
            "label": "Grouping results",
            "help_text": "All printed grouping results or revisions; cardinality is unconfirmed."
        },
        "supporting_documents_observations": {
            "label": "Supporting document observations",
            "help_text": "Presence, labels, identifiers, conflicts, and reviewer notes without embedded document content."
        }
    },
    "sources": {
        "PMK_26_2021_INA_CBG_GUIDE": {
            "label": "Permenkes 26/2021 — INA-CBG implementation guidance",
            "authority_note": "Official JDIH source; broad episode, coding, clinical-record, and INA-CBG concepts only."
        },
        "BPJS_REG_7_2018_CLAIM_ADMINISTRATION": {
            "label": "Peraturan BPJS Kesehatan 7/2018 — claim administration",
            "authority_note": "Government regulation repository source supporting RJTL and RITL document-requirement concepts; the pilot observation shape remains packet-unconfirmed."
        },
        "BPJS_JKN_SERVICE_GUIDE": {
            "label": "BPJS Kesehatan JKN service guide",
            "authority_note": "Official BPJS publication supporting hospital SEP issuance; it does not establish current packet or correction cardinality."
        },
        "BPJS_INTERFACE_CANDIDATE_UNCONFIRMED": {
            "label": "BPJS interface-workbook candidate mapping",
            "authority_note": "Discovery input only; not frozen, packet-confirmed, or authoritative."
        },
        "PMK_71_2013_JKN_HEALTH_SERVICE": {
            "label": "Permenkes 71/2013 — JKN health-service implementation",
            "authority_note": "Official BPK regulation record; supports public referral, proof-of-service, and cross-facility claim-document concepts."
        },
        "PMK_99_2015_JKN_HEALTH_SERVICE_AMENDMENT": {
            "label": "Permenkes 99/2015 — JKN health-service amendment",
            "authority_note": "Official BPK regulation record; supports prescription, dispensing, and receipt-document concepts without embedding a drug catalogue."
        },
        "PMK_3_2023_JKN_SERVICE_TARIFF": {
            "label": "Permenkes 3/2023 — JKN service tariffs",
            "authority_note": "Official BPK regulation record; supports payment-basis and service-category facts, not tariff-table embedding."
        },
        "PMK_16_2024_REFERRAL_SERVICE": {
            "label": "Permenkes 16/2024 — referral health services",
            "authority_note": "Official Kementerian Kesehatan JDIH source; supports minimum electronic-referral document fields."
        },
        "PERPRES_59_2024_JKN_BENEFITS": {
            "label": "Perpres 59/2024 — JKN benefits and coverage",
            "authority_note": "Official government regulation record; supports medical/non-medical benefit and care-setting concepts without deciding medical necessity."
        }
    }
}
